import axios from 'axios';

const TOKEN_KEYS = {
    ACCESS: 'access_token',
    REFRESH: 'refresh_token',
};

// Token management
export function saveTokens(access, refresh) {
    localStorage.setItem(TOKEN_KEYS.ACCESS, access);
    if (refresh) localStorage.setItem(TOKEN_KEYS.REFRESH, refresh);
}

export function getAccessToken() {
    return localStorage.getItem(TOKEN_KEYS.ACCESS);
}

export function getRefreshToken() {
    return localStorage.getItem(TOKEN_KEYS.REFRESH);
}

export function clearTokens() {
    localStorage.removeItem(TOKEN_KEYS.ACCESS);
    localStorage.removeItem(TOKEN_KEYS.REFRESH);
}

// Authenticated axios instance - use relative paths for same-origin requests
export const api = axios.create();
api.interceptors.request.use((config) => {
    const token = getAccessToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
});

// Auto-refresh access token on 401 and retry the failed request once.
// This keeps users signed in for the full 7-day refresh-token lifetime.
let refreshPromise = null;
api.interceptors.response.use(
    (res) => res,
    async (error) => {
        const original = error.config;
        const status = error.response?.status;
        // 402 → quota exceeded or upgrade required. Surface a global event so
        // any page can react (toast + upgrade modal). We DON'T retry these.
        if (status === 402) {
            const detail = error.response?.data?.detail || {};
            if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('app:upgrade-required', {
                    detail: {
                        code: detail.code,
                        kind: detail.kind,
                        used: detail.used,
                        limit: detail.limit,
                        tier: detail.tier,
                        feature: detail.feature,
                        message: detail.message,
                    },
                }));
            }
        }
        // Only retry on 401, never retry the refresh call itself, and retry once max
        if (
            status === 401 &&
            original &&
            !original._retry &&
            !original.url?.includes('/api/auth/refresh') &&
            !original.url?.includes('/api/auth/login')
        ) {
            original._retry = true;
            try {
                if (!refreshPromise) refreshPromise = refreshAccessToken();
                const newToken = await refreshPromise;
                refreshPromise = null;
                if (newToken) {
                    saveTokens(newToken, null);
                    original.headers.Authorization = `Bearer ${newToken}`;
                    return api(original);
                }
            } catch (_e) {
                refreshPromise = null;
            }
            // Refresh failed — clear tokens so user re-logs in
            clearTokens();
        }
        return Promise.reject(error);
    }
);

// API calls - use relative paths so they work on any domain
export async function loginApi(email, password) {
    const response = await axios.post('/api/auth/login', { email, password });
    return response.data;
}

export async function registerApi(name, email, password, role = 'student') {
    const response = await axios.post('/api/auth/register', { name, email, password, role });
    return response.data;
}

export async function fetchCurrentUser() {
    const response = await api.get('/api/auth/me');
    return response.data;
}

export async function refreshAccessToken() {
    const rt = getRefreshToken();
    if (!rt) return null;
    const response = await axios.post('/api/auth/refresh', {}, {
        headers: { 'X-Refresh-Token': rt }
    });
    return response.data.access_token;
}

export async function logoutApi() {
    await api.post('/api/auth/logout');
}

// Error formatting
export function formatApiErrorDetail(detail) {
    if (detail == null) return null;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail))
        return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
    if (detail && typeof detail.msg === 'string') return detail.msg;
    return String(detail);
}
